import { useState, useEffect } from 'react'
import { Coins, Plus, X, Loader2 } from 'lucide-react'
import api from '../lib/api'
import { useModal } from '../hooks/useModal'

interface CreditPackage {
  id: string
  name: string
  credits: number
  price: number
  price_cents: number
  per_credit: string
}

export default function CreditsBadge() {
  const [credits, setCredits] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [loadingPackages, setLoadingPackages] = useState(false)

  // Add escape key and body scroll prevention for the modal
  useModal({ isOpen: showModal, onClose: () => setShowModal(false) })

  useEffect(() => {
    fetchCredits()
  }, [])

  const fetchCredits = async () => {
    try {
      const response = await api.getPocCredits()
      if (response.success) {
        setCredits(response.balance)
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPackages = async () => {
    setLoadingPackages(true)
    try {
      const response = await api.getPocPackages()
      if (response.success) {
        setPackages(response.packages)
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error)
    } finally {
      setLoadingPackages(false)
    }
  }

  const handleOpenModal = () => {
    setShowModal(true)
    fetchPackages()
  }

  const handlePurchase = (pkg: CreditPackage) => {
    // TODO: Integrate with Stripe checkout
    alert(`Stripe integration coming soon! Package: ${pkg.name} - $${pkg.price}`)
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">...</span>
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
          <Coins className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-700">
            {credits ?? 0} credits
          </span>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Add</span>
        </button>
      </div>

      {/* Purchase Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowModal(false)}
            />

            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 z-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Purchase POC Credits
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Credits are used for vulnerability POC validation
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {loadingPackages ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : packages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No packages available
                </div>
              ) : (
                <div className="space-y-3">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => handlePurchase(pkg)}
                      className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                    >
                      <div>
                        <div className="font-medium text-gray-900">
                          {pkg.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {pkg.credits} credits @ ${pkg.per_credit}/credit
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-primary-600">
                        ${pkg.price.toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Coins className="h-4 w-4" />
                  <span>Current balance: {credits ?? 0} credits</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
